import { Awaited } from 'utils';
import { FUNC_BUTTONS } from 'paymentIntegrations/types';

import * as triposAPI from '../requests/triposRequests';

const waitForTerminalReady = async (lane, duration) => {
  const condition = { timedOut: false };

  const timeout = setTimeout(() => {
    condition.timedOut = true;
  }, duration);

  while (!condition.timedOut) {
    // eslint-disable-next-line no-await-in-loop
    const requestConnection = await triposAPI.requestCloudConnection({}, lane);
    if (
      // Successful request overall
      requestConnection.status === 200 &&
      // Endpoint responds that the connection is actually open and terminal can be used
      requestConnection.data?.status === 'Open'
    ) {
      break;
    }

    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => setTimeout(resolve, 750));
  }

  if (condition.timedOut) {
    throw new Error('Timeout while waiting for terminal ready');
  }

  clearTimeout(timeout);
};

export const handleErrors = ({
  err,
  displayError,
  updateMessage,
  enableButtons,
}) => {
  if (err.message === `Network Error`) {
    displayError(err.message);
    updateMessage(`Check connection with the terminal...`);
    enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
    return;
  }
  if (err.message === `Too Many Requests`) {
    displayError(`Too Many Requests`);
    updateMessage(`Too Many Requests. \n \n Please wait and try again...`);
    enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
    return;
  }
  if (err.message === `Transaction failed`) {
    displayError(`Transaction failed.`);
    updateMessage(`Transaction failed. \n \n Please try again...`);
    enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
    return;
  }
  if (err.message === `Transaction declined`) {
    displayError(`Transaction declined.`);
    updateMessage(`Transaction declined. \n \n Please try again...`);
    enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
    return;
  }
  if (err.response) {
    if (err.response.data.triPosError) {
      const { developerMessage: devMsg } = err.response.data.triPosError[0];
      displayError(`Transaction unsuccessful`);
      switch (devMsg) {
        case devMsg.includes(`The transaction has timed out`) && devMsg: {
          updateMessage(
            `The transaction has timed out. No request attempted. \n \n Please try again..`,
          );
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
        }
        case devMsg.includes(`Error getting signature`) && devMsg: {
          updateMessage(
            `Error getting signature. User may have pressed cancel. \n \n Please try again...`,
          );
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
        }
        case devMsg.includes(`Card is gift only card`) && devMsg: {
          updateMessage(
            `Card is gift only card but the configuration does not allow for gift transactions. \n \n Please try again...`,
          );
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
        }
        case devMsg.includes(`Card is debit only card`) && devMsg: {
          updateMessage(
            `Card is debit only card but the configuration does not allow for debit transactions. \n \n Please try again...`,
          );
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
        }
        case devMsg.includes(`Cancelled by cardholder`) && devMsg: {
          updateMessage(`Cancelled by cardholder. \n \n Please try again...`);
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
        }
        case devMsg.includes(
          `Card removed before transaction could complete`,
        ) && devMsg: {
          updateMessage(
            `Card was removed before processing was finished. \n \n Please try again...`,
          );
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
        }

        default:
          updateMessage(`Please try again...`);
          enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
          return;
      }
    }
    displayError(`Transaction unsuccessful.`);
    updateMessage(`Please try again...`);
    enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
    return;
  }
  if (err.message === 'Timeout while waiting for terminal ready') {
    displayError('Failed to get status of the lane.');
    updateMessage('Could not get status of the lane or lane is closed.');
    enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
    return;
  }
  displayError(`Transaction unsuccessful.`);
  updateMessage(`Please try again...`);
  enableButtons([FUNC_BUTTONS.RETRY, 'return-back']);
};

type WithResponseErrorsParams<
  RequestName extends
    | 'requestSale'
    | 'requestReversal'
    | 'requestReturn'
    | 'requestVoid',
  Response extends Awaited<ReturnType<typeof triposAPI[RequestName]>>
> = {
  requestName: RequestName;
  doRequest: () => Promise<Response>;
  laneId: string;
};

export function withResponseErrors<
  RequestName extends
    | 'requestSale'
    | 'requestReversal'
    | 'requestReturn'
    | 'requestVoid',
  Response extends Awaited<ReturnType<typeof triposAPI[RequestName]>>
>({
  requestName,
  doRequest,
  laneId,
}: WithResponseErrorsParams<RequestName, Response>): Promise<Response> {
  return new Promise((resolve, reject) => {
    function onResponse(requestName: RequestName, response: Response) {
      switch (requestName) {
        case 'requestSale': {
          // Successful
          if (Number(response.data.resultCode) === 0) {
            resolve(response);
            return;
          }
          let message = response.data.transactionStatus;
          if (response.data.transactionStatus === 'Failed') {
            message = 'Transaction failed';
          } else if (response.data.transactionStatus === 'Declined') {
            message = 'Transaction declined';
          } else if (response.data.transactionStatus === 'CardRemoved') {
            message = 'Card removed by customer';
          } else {
            message = response.data.transactionStatus;
          }
          const error = { response, message };
          reject(error);
          throw new Error(error.message);
        }
        case 'requestReversal':
        case 'requestReturn': {
          // Successful
          if (Number(response.data.resultCode) === 0) {
            resolve(response);
            return;
          }
          let message = response.data.transactionStatus;
          if (response.data.transactionStatus === 'Cancelled') {
            message = 'Transaction cancelled';
          } else if (response.data.transactionStatus === 'Failed') {
            message = 'Transaction failed';
          } else if (response.data.transactionStatus === 'Declined') {
            message = 'Transaction declined';
          }
          const error = { response, message };
          reject(error);
          throw new Error(error.message);
        }

        default:
          // Should probably reject by default
          resolve(response);
      }
    }

    waitForTerminalReady(laneId, 12000)
      .then(
        async () => {
          const response = await doRequest();
          onResponse(requestName, response);
        },
        err => {
          throw err;
        },
      )
      .catch(err => {
        reject(err);
      });
  });
}
